import * as React from "react";
import {CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line, ResponsiveContainer} from "recharts";
import {CircleHelp, Download, ChevronDown} from "lucide-react";
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {useIsMobile} from "@/hooks/use-mobile";
import {ChartContainer} from "@/components/ui/chart";
import {cn} from "@/lib/utils";
import axiosInstance from "@/api/axios";
import {forwardRef} from "react";
import {Button} from "@/components/ui/button";
import {useTranslation} from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CHART_COLORS: string[] = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(var(--chart-6))",
    "hsl(var(--chart-7))",
    "hsl(var(--chart-8))",
    "hsl(var(--chart-9))",
];

interface ProcessedChartData {
    period_label: string;
    [key: string]: string | number;
}

interface ChartLineInteractiveProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    title?: string;
    timeRange?: string;
    bins?: string[];
    customDateRange?: [{
        startDate: Date | null;
        endDate: Date | null;
        key: string;
    }];
    isCustomDateRange?: boolean;
}

interface BinColorMap {
    [key: string]: string;
}

interface ChartConfigItem {
    label: string;
    color: string;
}

interface DynamicChartConfig {
    [key: string]: ChartConfigItem;
}

interface ExportData {
    title: string;
    headers: string[];
    rows: string[][];
    chartData: {
        x: string;
        y: number;
    }[];
}

// Skeleton line paths — a handful of distinct wavy lines standing in for
// per-bin trend lines, not real data.
const SKELETON_LINES = [
    "M0,28 C10,20 20,24 30,14 C40,8 50,18 60,12 C70,6 80,16 90,10 L100,8",
    "M0,32 C10,34 20,26 30,30 C40,22 50,28 60,20 C70,24 80,16 90,20 L100,18",
    "M0,20 C10,26 20,18 30,22 C40,16 50,24 60,18 C70,22 80,14 90,18 L100,16",
];

export const WasteStreamProduction = forwardRef<HTMLDivElement, ChartLineInteractiveProps>(
    ({className, title, timeRange, bins = [], customDateRange, isCustomDateRange, ...props}, ref) => {
        const isMobile: boolean = useIsMobile();
        const {t} = useTranslation();
        const [processedData, setProcessedData] = React.useState<ProcessedChartData[]>([]);
        const [binColors, setBinColors] = React.useState<BinColorMap>({});
        const [selfAdjust, setSelfAdjust] = React.useState<boolean>(false)
        const [loading, setLoading] = React.useState<boolean>(false);
        const [error, setError] = React.useState<string | null>(null);

        const getChartTitle = (timeRange?: string): string => {
            if (isCustomDateRange) {
                return t('wasteProductionCustomRange') || t('wasteProduction');
            }
            switch (timeRange) {
                case 'Day':
                    return t('wasteProductionByHour');
                case 'Week':
                    return t('wasteProductionByDay');
                case 'Month':
                    return t('wasteProductionByWeek');
                case '3 Month':
                    return t('wasteProductionByMonth3');
                case '6 Month':
                    return t('wasteProductionByMonth6');
                case 'Year':
                    return t('wasteProductionByMonthYear');
                default:
                    return t('wasteProduction');
            }
        };

        const getXAxisLabel = (timeRange?: string): string => {
            if (isCustomDateRange) {
                return t('period');
            }
            switch (timeRange) {
                case 'Day':
                    return t('time');
                case 'Week':
                    return t('date');
                case 'Month':
                    return t('week');
                case '3 Month':
                case '6 Month':
                case 'Year':
                    return t('month');
                default:
                    return t('period');
            }
        };

        const chartTitle = title || getChartTitle(timeRange);
        const xAxisLabel = getXAxisLabel(timeRange);

        React.useEffect(() => {
            setProcessedData([]);
            setError(null);

            if (!bins || bins.length === 0) return;
            if (!timeRange && !isCustomDateRange) return;

            setLoading(true);

            const colorMap: BinColorMap = {};
            bins.forEach((bin, index) => {
                colorMap[bin] = CHART_COLORS[index % CHART_COLORS.length];
            });
            setBinColors(colorMap);

            const params: any = {
                bins: bins.join(",")
            };

            // Add either timeRange or custom date range (SAFE DATE HANDLING)
            if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
                const startDate = customDateRange[0].startDate;
                const endDate = customDateRange[0].endDate;

                if (startDate instanceof Date) {
                    params.startDate = startDate.toISOString();
                } else if (typeof startDate === 'string') {
                    params.startDate = startDate;
                } else {
                    // @ts-ignore
                    params.startDate = new Date(startDate).toISOString();
                }

                if (endDate instanceof Date) {
                    params.endDate = endDate.toISOString();
                } else if (typeof endDate === 'string') {
                    params.endDate = endDate;
                } else {
                    // @ts-ignore
                    params.endDate = new Date(endDate).toISOString();
                }
                
                console.log('WasteStreamProduction: Fetching with custom date range:', params);
            } else {
                params.timeRange = timeRange;
                console.log('WasteStreamProduction: Fetching with time range:', params);
            }

            axiosInstance
                .get("/waste_stream_production", {
                    params,
                    paramsSerializer: {indexes: null},
                })
                .then((response) => {
                    console.log("Waste Stream Data", response.data);

                    let arr = response.data[response.data.length - 1];
                    const shouldAdjust = arr.self_adjust;
                    setSelfAdjust(shouldAdjust);
                    console.log("Waste Stream Data last element", shouldAdjust);

                    let ResponseData = response.data
                    ResponseData.pop()
                    console.log("Waste Stream Data last element removed", ResponseData);

                    let dataArray: ProcessedChartData[] = Array.isArray(response.data)
                        ? ResponseData
                        : typeof ResponseData === "string"
                            ? JSON.parse(ResponseData)
                            : [];

                    const filledData = dataArray.map(item => {
                        const filledItem = {...item};
                        bins.forEach(bin => {
                            if (!(bin in filledItem)) filledItem[bin] = 0;
                        });
                        return filledItem;
                    });

                    setProcessedData(filledData);
                })
                .catch((error) => {
                    console.error("Error fetching waste stream data:", error);
                    setError(t('failedToFetchWasteData'));
                })
                .finally(() => setLoading(false));
        }, [timeRange, bins, customDateRange, isCustomDateRange, t]);

        const chartConfig: DynamicChartConfig = React.useMemo(() => {
            const config: DynamicChartConfig = {};
            bins.forEach((bin, index) => {
                config[bin] = {
                    label: `${t('bin')} ${bin}`,
                    color: CHART_COLORS[index % CHART_COLORS.length],
                };
            });
            return config;
        }, [bins, t]);

        const getExportData = React.useCallback((): ExportData => {
            const unit = selfAdjust ? 'kg' : 'g';
            const headers = [t('period'), ...bins.map(bin => `${t('bin')} ${bin} (${unit})`)];
            const rows = processedData.map(item => [
                `"${item.period_label}"`,
                ...bins.map(bin => Number(item[bin]).toFixed(3))
            ]);

            return {
                title: title || t('wasteProduction'),
                headers,
                rows,
                chartData: processedData.map(item => ({
                    x: item.period_label,
                    y: bins.reduce((sum, bin) => sum + (Number(item[bin]) || 0), 0)
                }))
            };
        }, [processedData, bins, title, selfAdjust, t]);

        const handleExportCSV = () => {
            const exportData = getExportData();
            if (!exportData) return;

            let csvContent = exportData.headers.join(',') + '\n';
            csvContent += exportData.rows.map(row => row.join(',')).join('\n');

            const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            const rangeLabel = isCustomDateRange ? 'custom' : (timeRange || 'data');
            link.setAttribute("download", `waste_production_${rangeLabel}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        const handleExportPNG = () => {
            const cardElement = document.querySelector('.exportable-section') as HTMLElement;
            const controls = cardElement?.querySelector('.export-controls') as HTMLElement;
            if (!cardElement) return;

            if (controls) controls.style.display = 'none';

            import('html2canvas').then((html2canvas) => {
                html2canvas.default(cardElement, {
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    allowTaint: true,
                }).then((canvas) => {
                    const link = document.createElement('a');
                    const rangeLabel = isCustomDateRange ? 'custom' : (timeRange || 'data');
                    link.download = `waste_production_chart_${rangeLabel}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                }).finally(() => {
                    if (controls) controls.style.display = '';
                });
            }).catch(err => {
                console.error('Failed to export as PNG:', err);
                if (controls) controls.style.display = '';
            });
        };

        if (loading) {
            return (
                <div ref={ref} className={cn("exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)} {...props}>
                    {/* Skeleton — header (title, export button, ? icon) stays
                        real; the plot area shows y-axis ticks, gridlines,
                        a few wavy lines standing in for per-bin trends, and
                        x-axis ticks, matching the loaded LineChart's shape. */}
                    <div className="relative">
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{chartTitle}</h4>
                            <div className="flex items-center space-x-2 export-controls">
                                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" disabled>
                                    <Download className="h-4 w-4 mr-1" />
                                    {t('export')}
                                    <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                                <TooltipProvider>
                                    <UITooltip>
                                        <TooltipTrigger asChild>
                                            <CircleHelp className="h-5 w-5 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('tracksWasteProductionTrends')}</p>
                                        </TooltipContent>
                                    </UITooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    </div>

                    <div
                        className="px-2 pt-4 sm:px-6 sm:pt-6 h-full w-full flex motion-safe:animate-pulse"
                        aria-busy="true"
                    >
                        <span className="sr-only" role="status">{t('loadingWasteData')}</span>

                        <div className="flex flex-col justify-between py-2 pr-2 w-10">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-2 w-full rounded bg-gray-200 dark:bg-slate-700" />
                            ))}
                        </div>

                        <div className="flex-1 flex flex-col min-w-0">
                            <div className="relative flex-1">
                                <div className="absolute inset-0 flex flex-col justify-between py-2">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <div key={i} className="h-px w-full bg-gray-200 dark:bg-slate-700" />
                                    ))}
                                </div>
                                <svg
                                    viewBox="0 0 100 40"
                                    preserveAspectRatio="none"
                                    className="absolute inset-0 w-full h-full"
                                    aria-hidden="true"
                                >
                                    {SKELETON_LINES.map((d, i) => (
                                        <path
                                            key={i}
                                            d={d}
                                            fill="none"
                                            className="stroke-gray-300 dark:stroke-slate-600"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    ))}
                                </svg>
                            </div>

                            <div className="flex justify-between pt-2 px-1">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className="h-2 w-8 rounded bg-gray-200 dark:bg-slate-700" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div ref={ref} className={cn("exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)} {...props}>
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{chartTitle}</h4>
                    <div className="flex h-full items-center justify-center">
                        <div className="text-destructive">{error}</div>
                    </div>
                </div>
            );
        }

        const exportData = getExportData();

        return (
            <div
                ref={ref}
                className={cn(
                    "exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4",
                    className
                )}
                data-export-data={JSON.stringify(exportData)}
                {...props}
            >
                <div className="relative">
                    <div className="flex justify-between items-center mb-2">
                        <div>
                            <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                                {chartTitle}
                            </h4>
                            {isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    {(() => {
                                        const startDate = customDateRange[0].startDate instanceof Date
                                            ? customDateRange[0].startDate
                                            : new Date(customDateRange[0].startDate);
                                        const endDate = customDateRange[0].endDate instanceof Date
                                            ? customDateRange[0].endDate
                                            : new Date(customDateRange[0].endDate);

                                        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                                            return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                                        }
                                        return '';
                                    })()}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center space-x-2 export-controls">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary"
                                    >
                                        <Download className="h-4 w-4 mr-1" />
                                        {t('export')}
                                        <ChevronDown className="h-4 w-4 ml-1" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={handleExportCSV} disabled={!exportData}>
                                        {t('downloadAsCSV')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleExportPNG} disabled={!exportData}>
                                        {t('downloadAsPNG')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <TooltipProvider>
                                <UITooltip>
                                    <TooltipTrigger asChild>
                                        <CircleHelp className="h-5 w-5 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{t('tracksWasteProductionTrends')}</p>
                                    </TooltipContent>
                                </UITooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </div>

                <div className="px-2 pt-4 sm:px-6 sm:pt-6" style={{ width: '100%', height: '100%' }}>
                    {!exportData ? (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            {t('noWasteDataAvailable')}
                        </div>
                    ) : (
                        <ChartContainer config={chartConfig} className="h-full w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={processedData}>
                                    <CartesianGrid vertical={false} stroke="lightgrey" />
                                    <XAxis
                                        dataKey="period_label"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        minTickGap={32}
                                        angle={timeRange === 'Day' || timeRange === 'Week' ? -45 : 0}
                                        textAnchor={timeRange === 'Day' || timeRange === 'Week' ? 'end' : 'middle'}
                                        height={timeRange === 'Day' || timeRange === 'Week' ? 80 : 60}
                                        interval={timeRange === 'Month' ? 'preserveStartEnd' : 0}
                                    />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `${value} ${selfAdjust ? 'kg' : 'g'}`}
                                    />
                                    <Tooltip
                                        cursor={{ strokeDasharray: "3 3" }}
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length > 0) {
                                                return (
                                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                                        <div className="font-medium mb-1">{xAxisLabel}: {label}</div>
                                                        {payload.map((entry, index) => (
                                                            <div key={`item-${index}`} className="flex items-center gap-2 text-sm">
                                                                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                                                <span>{t('bin')} {entry.dataKey}: </span>
                                                                <span className="font-medium">{(Number(entry.value) || 0).toFixed(3)} {selfAdjust ? 'kg' : 'g'}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                   {bins.map((bin) => (
                                        <Line
                                            key={bin}
                                            type="monotone"
                                            dataKey={bin}
                                            stroke={chartConfig[bin].color}
                                            strokeWidth={2}
                                            dot={{
                                                r: 4,
                                                strokeWidth: 2,
                                                stroke: "white",
                                                fill: chartConfig[bin].color,
                                            }}
                                            activeDot={{
                                                r: 6,
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                            name={bin}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    )}
                </div>
            </div>
        );
    }
);

WasteStreamProduction.displayName = "WasteStreamProduction";